import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/components/ui/use-toast';
import { Tv, Settings, Palette, Zap, Eye, EyeOff, RefreshCw } from 'lucide-react';

const KaraokeTVControlPanel = () => {
  const { toast } = useToast();
  const [settingsHydrated, setSettingsHydrated] = useState(false);
  const [seasonCheckMonth, setSeasonCheckMonth] = useState(() => new Date().getMonth());
  const [settings, setSettings] = useState({
    preset: 'default',
    primaryColor: '#7c3aed',
    secondaryColor: '#4f46e5',
    accentColor: '#ec4899',
    floatingNotes: true,
    waveLines: true,
    particles: true,
    colorShifts: true,
    gridPattern: true,
    animationSpeed: 3,
    particleDensity: 3,
    noteOpacity: 0.2,
    showQRCode: true,
    showSocialLinks: true,
    showVenueInfo: true,
    showRequestQueue: true,
    fontSize: 'large',
    cardTransparency: 0.8,
    backdropBlur: 10
  });

  const presets = {
    default: {
      name: 'Default Karaoke',
      description: 'Classic purple theme with moderate animations',
      colors: { primary: '#7c3aed', secondary: '#4f46e5', accent: '#ec4899' },
      animations: { speed: 3, density: 3, opacity: 0.2 }
    },
    neon: {
      name: 'Neon Nightclub',
      description: 'Bright neon colors with high energy animations',
      colors: { primary: '#00ff88', secondary: '#ff0080', accent: '#00ffff' },
      animations: { speed: 5, density: 5, opacity: 0.4 }
    },
    elegant: {
      name: 'Elegant Lounge',
      description: 'Sophisticated dark theme with subtle animations',
      colors: { primary: '#1f2937', secondary: '#374151', accent: '#8b5cf6' },
      animations: { speed: 2, density: 2, opacity: 0.1 }
    },
    party: {
      name: 'Party Mode',
      description: 'Vibrant colors with high energy effects',
      colors: { primary: '#f59e0b', secondary: '#ef4444', accent: '#10b981' },
      animations: { speed: 4, density: 4, opacity: 0.3 }
    },
    minimal: {
      name: 'Minimal Clean',
      description: 'Simple, clean design with minimal animations',
      colors: { primary: '#6b7280', secondary: '#9ca3af', accent: '#d1d5db' },
      animations: { speed: 1, density: 1, opacity: 0.05 }
    },
    retro: {
      name: 'Retro Vibes',
      description: '80s inspired colors with classic animations',
      colors: { primary: '#ff6b6b', secondary: '#4ecdc4', accent: '#45b7d1' },
      animations: { speed: 3, density: 4, opacity: 0.25 }
    },
    christmas: {
      name: '🎄 Very Christmas 🎄',
      description: 'ULTRA Christmas theme with snow, lights & festive colors!',
      colors: { primary: '#dc2626', secondary: '#16a34a', accent: '#fbbf24' },
      animations: { speed: 6, density: 8, opacity: 0.6, notes: true, waves: true, particles: true, grid: false, shifts: true }
    }
  };

  const updateTVDisplay = (settingsToApply = settings, options = {}) => {
    const silent = options.silent === true;
    try {
      // Save settings to localStorage
      localStorage.setItem('tv-display-settings', JSON.stringify(settingsToApply));
      
      // Try to refresh the main TV Display window
      const tvWindow = window.opener || window.parent;
      if (tvWindow && !tvWindow.closed) {
        try {
          // Send a custom event to refresh the TV display
          tvWindow.postMessage({ type: 'REFRESH_TV_DISPLAY' }, '*');
        } catch (e) {
          console.log('Could not send message to TV display window');
        }
      }
      
      if (!silent) {
        toast({
          title: "Settings Updated! ✨",
          description: "TV Display will refresh with new settings",
          variant: "default"
        });
      }
      
    } catch (error) {
      console.error('Error updating TV display:', error);
      toast({
        title: "Error",
        description: "Failed to update TV display settings",
        variant: "destructive"
      });
    }
  };

  const applyPreset = (presetKey, options = {}) => {
    const silent = options.silent === true;
    const preset = presets[presetKey];
    if (preset) {
      const newSettings = {
        ...settings,
        preset: presetKey,
        primaryColor: preset.colors.primary,
        secondaryColor: preset.colors.secondary,
        accentColor: preset.colors.accent,
        animationSpeed: preset.animations.speed,
        particleDensity: preset.animations.density,
        noteOpacity: preset.animations.opacity
      };
      
      setSettings(newSettings);
      
      // Apply preset instantly without needing to press Apply
      updateTVDisplay(newSettings, { silent });
      
      if (!silent) {
        toast({
          title: "Preset Applied! 🎨",
          description: `${preset.name} applied instantly`,
          variant: "default"
        });
      }
    }
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem('tv-display-settings');
      if (raw) {
        const parsed = JSON.parse(raw);
        setSettings((prev) => ({ ...prev, ...parsed }));
      }
    } catch {
      /* ignore corrupt storage */
    }
    setSettingsHydrated(true);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      setSeasonCheckMonth(new Date().getMonth());
    }, 30 * 60 * 1000);
    return () => window.clearInterval(id);
  }, []);

  /** Not December: drop Christmas preset if it was saved from last year. */
  useEffect(() => {
    if (!settingsHydrated) return;
    if (seasonCheckMonth !== 11 && settings.preset === 'christmas') {
      applyPreset('default', { silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsHydrated, seasonCheckMonth, settings.preset]);

  const resetToDefault = () => {
    const defaultSettings = {
      preset: 'default',
      primaryColor: '#7c3aed',
      secondaryColor: '#4f46e5',
      accentColor: '#ec4899',
      floatingNotes: true,
      waveLines: true,
      particles: true,
      colorShifts: true,
      gridPattern: true,
      animationSpeed: 3,
      particleDensity: 3,
      noteOpacity: 0.2,
      showQRCode: true,
      showSocialLinks: true,
      showVenueInfo: true,
      showRequestQueue: true,
      fontSize: 'large',
      cardTransparency: 0.8,
      backdropBlur: 10
    };
    
    setSettings(defaultSettings);
    updateTVDisplay(defaultSettings);
    
    toast({
      title: "Reset Complete! 🔄",
      description: "All settings reset to default",
      variant: "default"
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <Card className="bg-purple-800/50 border-purple-600">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <Tv className="w-6 h-6 text-blue-400" />
              TV Display Control Panel
            </CardTitle>
            <p className="text-purple-200 text-sm">
              Customize your karaoke TV display in real-time
            </p>
          </CardHeader>
        </Card>

        {/* Presets */}
        <Card className="bg-purple-800/50 border-purple-600">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-blue-400" />
              Quick Presets
            </CardTitle>
            <p className="text-purple-200 text-sm">
              Click any preset to apply instantly. The Christmas preset only appears in December (device calendar).
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(presets)
                .filter(([key]) => key !== 'christmas' || seasonCheckMonth === 11)
                .map(([key, preset]) => (
                <Button
                  key={key}
                  onClick={() => applyPreset(key)}
                  variant={settings.preset === key ? "default" : "outline"}
                  className={`h-auto p-3 flex flex-col items-center gap-2 ${
                    settings.preset === key 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'border-purple-600 text-purple-300 hover:bg-purple-600/20'
                  }`}
                >
                  <div className="flex gap-1">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: preset.colors.primary }}
                    />
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: preset.colors.secondary }}
                    />
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: preset.colors.accent }}
                    />
                  </div>
                  <div className="text-xs">
                    <div className="font-semibold">{preset.name}</div>
                    <div className="text-xs opacity-75">{preset.description}</div>
                  </div>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Colors */}
        <Card className="bg-purple-800/50 border-purple-600">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-blue-400" />
              Custom Colors
            </CardTitle>
            <p className="text-purple-200 text-sm">
              Customize your color scheme
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="primaryColor" className="text-purple-200">Primary</Label>
                <Input
                  id="primaryColor"
                  type="color"
                  value={settings.primaryColor}
                  onChange={(e) => setSettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                  className="h-12 border-purple-600 bg-purple-900"
                />
              </div>
              <div>
                <Label htmlFor="secondaryColor" className="text-purple-200">Secondary</Label>
                <Input
                  id="secondaryColor"
                  type="color"
                  value={settings.secondaryColor}
                  onChange={(e) => setSettings(prev => ({ ...prev, secondaryColor: e.target.value }))}
                  className="h-12 border-purple-600 bg-purple-900"
                />
              </div>
              <div>
                <Label htmlFor="accentColor" className="text-purple-200">Accent</Label>
                <Input
                  id="accentColor"
                  type="color"
                  value={settings.accentColor}
                  onChange={(e) => setSettings(prev => ({ ...prev, accentColor: e.target.value }))}
                  className="h-12 border-purple-600 bg-purple-900"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Animations */}
        <Card className="bg-purple-800/50 border-purple-600">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-blue-400" />
              Animation Settings
            </CardTitle>
            <p className="text-purple-200 text-sm">
              Control the visual effects and animations
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Animation Toggles */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="floatingNotes"
                  checked={settings.floatingNotes}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, floatingNotes: checked }))}
                />
                <Label htmlFor="floatingNotes" className="text-purple-200">Floating Notes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="waveLines"
                  checked={settings.waveLines}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, waveLines: checked }))}
                />
                <Label htmlFor="waveLines" className="text-purple-200">Wave Lines</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="particles"
                  checked={settings.particles}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, particles: checked }))}
                />
                <Label htmlFor="particles" className="text-purple-200">Particles</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="colorShifts"
                  checked={settings.colorShifts}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, colorShifts: checked }))}
                />
                <Label htmlFor="colorShifts" className="text-purple-200">Color Shifts</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="gridPattern"
                  checked={settings.gridPattern}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, gridPattern: checked }))}
                />
                <Label htmlFor="gridPattern" className="text-purple-200">Grid Pattern</Label>
              </div>
            </div>

            {/* Animation Sliders */}
            <div className="space-y-4">
              <div>
                <Label className="text-purple-200">Animation Speed</Label>
                <Slider
                  value={[settings.animationSpeed]}
                  onValueChange={([value]) => setSettings(prev => ({ ...prev, animationSpeed: value }))}
                  max={5}
                  min={1}
                  step={1}
                  className="w-full"
                />
                <div className="text-xs text-purple-300 mt-1">
                  {settings.animationSpeed === 1 ? 'Slow' : 
                   settings.animationSpeed === 3 ? 'Normal' : 
                   settings.animationSpeed === 5 ? 'Fast' : 'Custom'}
                </div>
              </div>
              
              <div>
                <Label className="text-purple-200">Particle Density</Label>
                <Slider
                  value={[settings.particleDensity]}
                  onValueChange={([value]) => setSettings(prev => ({ ...prev, particleDensity: value }))}
                  max={5}
                  min={1}
                  step={1}
                  className="w-full"
                />
                <div className="text-xs text-purple-300 mt-1">
                  {settings.particleDensity === 1 ? 'Minimal' : 
                   settings.particleDensity === 3 ? 'Moderate' : 
                   settings.particleDensity === 5 ? 'Dense' : 'Custom'}
                </div>
              </div>
              
              <div>
                <Label className="text-purple-200">Note Opacity</Label>
                <Slider
                  value={[settings.noteOpacity]}
                  onValueChange={([value]) => setSettings(prev => ({ ...prev, noteOpacity: value }))}
                  max={0.8}
                  min={0.1}
                  step={0.05}
                  className="w-full"
                />
                <div className="text-xs text-purple-300 mt-1">
                  {Math.round(settings.noteOpacity * 100)}% opacity
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content Visibility */}
        <Card className="bg-purple-800/50 border-purple-600">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-400" />
              Content Visibility
            </CardTitle>
            <p className="text-purple-200 text-sm">
              Show or hide different elements on the TV display
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="showQRCode"
                  checked={settings.showQRCode}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showQRCode: checked }))}
                />
                <Label htmlFor="showQRCode" className="text-purple-200">QR Code</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="showSocialLinks"
                  checked={settings.showSocialLinks}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showSocialLinks: checked }))}
                />
                <Label htmlFor="showSocialLinks" className="text-purple-200">Social Links</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="showVenueInfo"
                  checked={settings.showVenueInfo}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showVenueInfo: checked }))}
                />
                <Label htmlFor="showVenueInfo" className="text-purple-200">Venue Info</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="showRequestQueue"
                  checked={settings.showRequestQueue}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showRequestQueue: checked }))}
                />
                <Label htmlFor="showRequestQueue" className="text-purple-200">Request Queue</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Display Settings */}
        <Card className="bg-purple-800/50 border-purple-600">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-400" />
              Display Settings
            </CardTitle>
            <p className="text-purple-200 text-sm">
              Fine-tune the visual appearance
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="fontSize" className="text-purple-200">Font Size</Label>
              <Select value={settings.fontSize} onValueChange={(value) => setSettings(prev => ({ ...prev, fontSize: value }))}>
                <SelectTrigger className="border-purple-600 bg-purple-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-purple-900 border-purple-600">
                  <SelectItem value="small">Small</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="large">Large</SelectItem>
                  <SelectItem value="xlarge">Extra Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-purple-200">Card Transparency</Label>
              <Slider
                value={[settings.cardTransparency]}
                onValueChange={([value]) => setSettings(prev => ({ ...prev, cardTransparency: value }))}
                max={1}
                min={0.1}
                step={0.1}
                className="w-full"
              />
              <div className="text-xs text-purple-300 mt-1">
                {Math.round(settings.cardTransparency * 100)}% opacity
              </div>
            </div>
            
            <div>
              <Label className="text-purple-200">Backdrop Blur</Label>
              <Slider
                value={[settings.backdropBlur]}
                onValueChange={([value]) => setSettings(prev => ({ ...prev, backdropBlur: value }))}
                max={20}
                min={0}
                step={1}
                className="w-full"
              />
              <div className="text-xs text-purple-300 mt-1">
                {settings.backdropBlur}px blur
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Card className="bg-purple-800/50 border-purple-600">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Button
                onClick={() => updateTVDisplay()}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Apply Changes
              </Button>
              <Button
                onClick={resetToDefault}
                variant="outline"
                className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
              >
                Reset to Default
              </Button>
            </div>
            <p className="text-xs text-purple-400 mt-2 text-center">
              💡 Presets apply instantly • Manual changes require "Apply Changes"
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default KaraokeTVControlPanel;
